import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getBoxLandingPageMemberships,
  getConsentCopy,
  getGymTermsSummary,
} from "@lemonadetraining/web-shared";
import type { GymTermsSummary } from "@lemonadetraining/contracts";
import {
  describeCommitmentReview,
  describeMonthlyReview,
  isCommitmentLine,
  isRecurringGymLine,
  type CommitmentReview,
  type CommitmentReviewProduct,
  type MembershipCartLine,
  type MonthlyReview,
} from "@lemonadetraining/web-shared/utils/commitmentPlans";
import {
  CHECKOUT_CONSENT_LOCALE,
  CONSENT_COPY_SCOPE,
  consentReasonOf,
  isUsableConsentCopy,
  type ConsentCopy,
  type ConsentReason,
} from "@lemonadetraining/web-shared/utils/commitmentConsent";

/**
 * The two things the checkout must know before it may sell a commitment, and
 * neither of which the cart is carrying: what the gym's terms are RIGHT NOW,
 * and the exact consent wording the server will hold the buyer to.
 *
 * Both are fetched when the modal opens rather than when the item was added.
 * A cart can sit open for an hour while the gym edits its pricing or the
 * platform revises its consumer-rights copy, and the review is the last thing
 * the buyer reads before paying — stale figures there are not a display bug,
 * they are a misrepresentation of a contract.
 */

/* One entry of getBoxLandingPageMemberships: what the gym sells right now, minus the buyer roster. */
interface SoldMembership {
  id: string;
  data: CommitmentReviewProduct;
}

/* Keyed by membership id; null when the gym's catalogue could not be read. */
type Catalogue = Map<string, CommitmentReviewProduct> | null;

// The gyms to ask, once each. A line from before memberships carried their
// gym has no id to ask about, so it is left out.
const distinctBoxKey = (lines: MembershipCartLine[]): string =>
  Array.from(
    new Set(lines.map((line) => line.boxID).filter((boxID): boxID is string => Boolean(boxID)))
  )
    .sort()
    .join("|");

export type ConsentCopyState = "idle" | "loading" | "ready" | "error";

/* `error` renders nothing before submit — the link is additive and the server
 * decides. It exists so a failed lookup is never read as "nothing required". */
export type GymTermsState = "idle" | "loading" | "ready" | "error";

export interface CheckoutReviewLine {
  line: MembershipCartLine;
  review: CommitmentReview;
}

export interface CheckoutMonthlyLine {
  line: MembershipCartLine;
  review: MonthlyReview;
}

export interface CheckoutReview {
  /** One entry per commitment line in the cart, in cart order. */
  lines: CheckoutReviewLine[];
  /** True while the products are being re-read, and until every one of them has been. */
  productsLoading: boolean;
  /** One entry per rolling month-to-month gym line, once the served copy covers them. */
  monthlyLines: CheckoutMonthlyLine[];
  /** This cart holds a commitment, so it cannot be sold without a consent claim. */
  needsConsent: boolean;
  /**
   * Whether the statutory blocks belong on screen at all: a commitment, or a
   * recurring gym line once the SERVER says its consent gate covers those.
   */
  needsStatutory: boolean;
  /** Whether re-reading the copy could turn a server-side consent refusal into a checkbox. */
  hasRecurringGymLines: boolean;
  copy: ConsentCopy | null;
  copyState: ConsentCopyState;
  /** Why the copy fetch failed, when the server gave a reason. */
  copyFailureReason: ConsentReason | null;
  /** Read both the products and the consent copy again — after a retry, or after the server said the terms moved. */
  refetchReview: () => void;
  /**
   * Changes whenever anything the buyer is consenting TO changes. Ticking a box
   * against one set of terms must never carry over to another.
   */
  consentSignature: string;
  /** Keyed by gym id, and only for gyms that actually publish terms. */
  termsByBoxID: Record<string, GymTermsSummary>;
  termsState: GymTermsState;
  /** Read the gyms' terms again without disturbing the consent copy. */
  refetchTerms: () => void;
}

export function useCheckoutReview(cartItems: MembershipCartLine[], open: boolean): CheckoutReview {
  const commitmentLines = useMemo(() => (cartItems || []).filter(isCommitmentLine), [cartItems]);
  /* Every line sold by a gym, whatever it is: what the gym's own terms cover. */
  const gymLines = useMemo(
    () => (cartItems || []).filter((line) => Boolean(line.boxID)),
    [cartItems]
  );
  const recurringGymLines = useMemo(() => gymLines.filter(isRecurringGymLine), [gymLines]);
  const hasRecurringGymLines = recurringGymLines.length > 0;
  const needsConsent = commitmentLines.length > 0;

  const [catalogues, setCatalogues] = useState<Map<string, Catalogue>>(() => new Map());
  const [productsLoading, setProductsLoading] = useState(false);
  const [copy, setCopy] = useState<ConsentCopy | null>(null);
  const [copyState, setCopyState] = useState<ConsentCopyState>("idle");
  const [copyFailureReason, setCopyFailureReason] = useState<ConsentReason | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [termsByBoxID, setTermsByBoxID] = useState<Record<string, GymTermsSummary>>({});
  const [termsState, setTermsState] = useState<GymTermsState>("idle");
  const [termsRetryToken, setTermsRetryToken] = useState(0);

  /* The SERVER owns the scope of its own gate; this only reads the answer.
     An absent `scope` is the narrow setting, so a copy from a server predating
     the field can never put a statutory block on screen it will not enforce. */
  const monthlyInScope = copy?.scope === CONSENT_COPY_SCOPE.allRecurring && hasRecurringGymLines;
  /* A commitment in the cart earns the statutory blocks on its own, but it must
     NOT drag its monthly siblings into a review panel the server is not gating:
     at the narrow scope a mixed cart has to look exactly as it does today. */
  const needsStatutory = needsConsent || monthlyInScope;

  // Bumping this re-reads BOTH halves. A recovery happens because something
  // moved on the server while this screen was open, and the buyer is about to
  // re-confirm — so everything they are re-confirming against should be current,
  // not just the half that reported the problem.
  const refetchReview = useCallback(() => {
    setRetryToken((token) => token + 1);
  }, []);

  const refetchTerms = useCallback(() => {
    setTermsRetryToken((token) => token + 1);
  }, []);

  /* The lines a review panel will be drawn for. The monthly ones join only once
     the served copy has widened the scope, so a checkout that is going to show
     them nothing costs no reads at all. */
  const reviewLines = useMemo(
    () => (monthlyInScope ? [...commitmentLines, ...recurringGymLines] : commitmentLines),
    [monthlyInScope, commitmentLines, recurringGymLines]
  );

  /* One entry per distinct product: a commitment cell and a monthly cell of the
     same membership share a programUID. */
  const reviewTargets = useMemo(
    () => [...new Map(reviewLines.map((line) => [line.programUID, line])).values()],
    [reviewLines]
  );
  // Kept as a string so the fetch re-runs when the SET of gyms changes, not when
  // the cart array is rebuilt or a new product of an already-read gym joins.
  const reviewBoxKey = distinctBoxKey(reviewTargets);
  const termsBoxKey = distinctBoxKey(gymLines);

  useEffect(() => {
    if (!open || !reviewBoxKey) {
      // Preserve the empty map's identity: this component is mounted
      // app-wide, and a fresh one here re-renders it on every cart change made
      // while the modal is shut.
      setCatalogues((prev) => (prev.size ? new Map() : prev));
      setProductsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setProductsLoading(true);

    const boxIDs = reviewBoxKey.split("|");
    Promise.all(
      boxIDs.map(async (boxID): Promise<[string, Catalogue]> => {
        try {
          const result = await getBoxLandingPageMemberships(boxID);
          if (!Array.isArray(result?.data)) return [boxID, null];
          const sold = result.data as SoldMembership[];
          return [boxID, new Map(sold.map(({ id, data }) => [id, data]))];
        } catch {
          return [boxID, null];
        }
      })
    ).then((results) => {
      if (cancelled) return;
      setCatalogues(new Map(results));
      setProductsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [open, reviewBoxKey, retryToken]);

  // A line with no gym, a failed lookup, or a product the gym no longer sells
  // is null: "unknown" everywhere downstream, rendered as unknown, never as a
  // default that would put invented terms on a contract. A line whose gym has
  // not answered yet is absent, which reads as still loading.
  const products = useMemo(() => {
    const next: Record<string, CommitmentReviewProduct | null> = {};
    for (const line of reviewTargets) {
      if (!line.boxID) next[line.programUID] = null;
      else if (catalogues.has(line.boxID)) {
        next[line.programUID] = catalogues.get(line.boxID)?.get(line.programUID) ?? null;
      }
    }
    return next;
  }, [reviewTargets, catalogues]);

  /* Fired for a recurring gym cart too, because the scope of the gate is the
     server's answer to give and this is how it arrives. For a cart with no
     commitment in it the result is purely additive: nothing waits on it, and a
     failure leaves the checkout exactly as it was. */
  const wantsCopy = needsConsent || hasRecurringGymLines;

  useEffect(() => {
    if (!open || !wantsCopy) {
      setCopy(null);
      setCopyState("idle");
      setCopyFailureReason(null);
      return undefined;
    }

    let cancelled = false;
    setCopyState("loading");
    setCopyFailureReason(null);

    getConsentCopy(CHECKOUT_CONSENT_LOCALE)
      .then((data: unknown) => {
        if (cancelled) return;
        if (isUsableConsentCopy(data)) {
          setCopy(data);
          setCopyState("ready");
          return;
        }
        // A half-populated response would render an unlabelled checkbox in
        // front of someone signing a year-long contract. Treat it as a failure.
        setCopy(null);
        setCopyState("error");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setCopy(null);
        setCopyState("error");
        setCopyFailureReason(consentReasonOf(error));
      });

    return () => {
      cancelled = true;
    };
  }, [open, wantsCopy, retryToken]);

  useEffect(() => {
    if (!open || !termsBoxKey) {
      setTermsByBoxID((prev) => (Object.keys(prev).length ? {} : prev));
      setTermsState("idle");
      return undefined;
    }

    let cancelled = false;
    setTermsState("loading");

    const boxIDs = termsBoxKey.split("|");
    Promise.allSettled(boxIDs.map((boxID) => getGymTermsSummary({ boxID }))).then((results) => {
      if (cancelled) return;
      const next: Record<string, GymTermsSummary> = {};
      results.forEach((result, index) => {
        // Swallowed on purpose. The consent copy is legally required and its
        // absence must stop the sale; this link is additive, so a failed or
        // absent lookup shows nothing rather than alarming a paying buyer.
        if (result.status !== "fulfilled") return;
        const summary = result.value as GymTermsSummary | null;
        if (summary && summary.hasTerms) next[boxIDs[index]] = summary;
      });
      setTermsByBoxID(next);
      // Knowing nothing is not the same as knowing nothing is required.
      const allFailed = results.every((result) => result.status !== "fulfilled");
      setTermsState(allFailed ? "error" : "ready");
    });

    return () => {
      cancelled = true;
    };
    // retryToken rides along: refetchReview happens because something moved on
    // the server, and everything the buyer re-confirms against should be current.
  }, [open, termsBoxKey, retryToken, termsRetryToken]);

  const lines = useMemo(() => {
    const described: CheckoutReviewLine[] = [];
    for (const line of commitmentLines) {
      const review = describeCommitmentReview(line, products[line.programUID]);
      if (review) described.push({ line, review });
    }
    return described;
  }, [commitmentLines, products]);

  const monthlyLines = useMemo(() => {
    if (!monthlyInScope) return [];
    const described: CheckoutMonthlyLine[] = [];
    for (const line of recurringGymLines) {
      const review = describeMonthlyReview(line, products[line.programUID]);
      if (review) described.push({ line, review });
    }
    return described;
  }, [monthlyInScope, recurringGymLines, products]);

  /* `productsLoading` alone leaves a frame between the copy widening the scope
     and the effect that reads the newly-in-scope products, in which nothing is
     loading and nothing is there. Ask the data instead of the flag. */
  const productsPending = reviewLines.some((line) => !(line.programUID in products));

  const consentSignature = useMemo(
    () =>
      [
        open ? "open" : "closed",
        copy?.consentWordingHash || "",
        copy?.withdrawalInfoHash || "",
        /* The scope and the covered set ride along: a tick given while one set
           of lines was being consented to must never carry over to another. */
        copy?.scope || "",
        needsStatutory ? "statutory" : "none",
        ...monthlyLines.map(
          ({ line, review }) =>
            `${line.programUID}:${line.selectedOptionKey}:${line.priceID}:${review.monthlyAmount}`
        ),
        ...lines.map(
          ({ line, review }) =>
            `${line.programUID}:${line.selectedOptionKey}:${line.priceID}:` +
            `${review.instalmentAmount}:${review.total ?? ""}:${review.endBehavior ?? ""}:` +
            `${review.commitmentMonths}:${review.paymentMode ?? ""}:${
              review.rollMonthlyAmount ?? ""
            }`
        ),
      ].join("|"),
    [open, copy, lines, monthlyLines, needsStatutory]
  );

  return {
    lines,
    monthlyLines,
    productsLoading: productsLoading || productsPending,
    needsConsent,
    needsStatutory,
    hasRecurringGymLines,
    copy,
    copyState,
    copyFailureReason,
    refetchReview,
    consentSignature,
    termsByBoxID,
    termsState,
    refetchTerms,
  };
}
